import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { logos } from '../../assets/images';

const CreateAccount = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: ''
  });
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Check if user was redirected from login page or newsletter signup
  useEffect(() => {
    if ((location.state?.fromLogin || location.state?.fromNewsletter) && location.state?.message) {
      setInfoMessage(location.state.message);
      if (location.state.email) {
        setFormData(prev => ({ ...prev, email: location.state.email }));
      }
      // Clear the state so message doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Use email as username
    const registrationData = {
      ...formData,
      username: formData.email
    };
    const result = await register(registrationData);
    if (result.success) {
      navigate('/registration-success', { state: { email: formData.email } });
    } else {
      // Log the error for debugging
      console.log('Registration error:', result.error);
      console.log('Error type:', typeof result.error);

      // Format error message to be human-friendly
      let errorMessage = '';
      if (typeof result.error === 'object' && result.error !== null) {
        // Group errors by field
        const errorsByField = {};
        for (const [field, messages] of Object.entries(result.error)) {
          const fieldName = field === 'username' ? 'Email' : field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
          if (Array.isArray(messages)) {
            errorsByField[fieldName] = messages;
          } else {
            errorsByField[fieldName] = [messages];
          }
        }

        // Format each field's errors
        const formattedErrors = [];
        for (const [field, messages] of Object.entries(errorsByField)) {
          if (messages.length === 1) {
            formattedErrors.push(`${field}: ${messages[0]}`);
          } else {
            // Multiple errors for same field - combine them with proper grammar
            const allButLast = messages.slice(0, -1).join(', ');
            const last = messages[messages.length - 1];
            formattedErrors.push(`${field}: ${allButLast}, and ${last.toLowerCase()}`);
          }
        }

        errorMessage = formattedErrors.length > 0 ? formattedErrors.join(' | ') : 'Registration failed. Please check your information and try again.';
      } else if (typeof result.error === 'string' && result.error !== 'Registration failed') {
        errorMessage = result.error;
      } else {
        errorMessage = 'Registration failed. Please check your information and try again.';
      }
      setError(errorMessage);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="register-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', minHeight: '0' }}>
      <div className="container" style={{
        maxWidth: '380px',
        width: '100%',
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <img
            src={logos.UBLong}
            alt="UnsupervisedBias"
            style={{
              maxWidth: '280px',
              width: '100%',
              height: 'auto',
              marginBottom: '6px'
            }}
          />
          <p style={{ fontSize: '16px', margin: '0', fontWeight: '400', opacity: 0.9 }}>Welcome! What's your name?</p>
        </div>
        {infoMessage && <div className="info" style={{
          backgroundColor: '#e3f2fd',
          color: '#1565c0',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '12px',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>{infoMessage}</span>
          <button
            onClick={() => setInfoMessage('')}
            style={{
              background: 'none',
              border: 'none',
              color: '#1565c0',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0 5px'
            }}
          >
            ×
          </button>
        </div>}
        {error && <div className="error" style={{
          backgroundColor: '#ffebee',
          color: '#c62828',
          padding: '10px',
          borderRadius: '8px',
          marginBottom: '10px',
          textAlign: 'left'
        }}>
          {error.split(' | ').map((err, index) => (
            <div key={index} style={{ marginBottom: index < error.split(' | ').length - 1 ? '6px' : '0' }}>
              {err}
            </div>
          ))}
        </div>}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <input
            name="email"
            type="email"
            placeholder="Email"
            value={formData.email}
            required
            onChange={handleChange}
            style={{
              padding: '12px',
              fontSize: '16px',
              borderRadius: '8px',
              border: '1px solid #ddd'
            }}
          />
          <input
            name="first_name"
            placeholder="First Name"
            onChange={handleChange}
            style={{
              padding: '12px',
              fontSize: '16px',
              borderRadius: '8px',
              border: '1px solid #ddd'
            }}
          />
          <input
            name="last_name"
            placeholder="Last Name"
            onChange={handleChange}
            style={{
              padding: '12px',
              fontSize: '16px',
              borderRadius: '8px',
              border: '1px solid #ddd'
            }}
          />
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              required
              onChange={handleChange}
              style={{
                padding: '12px 45px 12px 12px',
                fontSize: '16px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '5px',
                display: 'flex',
                alignItems: 'center',
                color: '#666'
              }}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              )}
            </button>
          </div>
          <button
            type="submit"
            style={{
              padding: '15px 30px',
              fontSize: '18px',
              border: 'none',
              outline: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, var(--pink) 0%, var(--violet) 100%)',
              color: '#fff',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(216, 134, 255, 0.4)',
              transition: 'all 0.3s ease',
              // textTransform: 'uppercase',
              letterSpacing: '1px',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              boxSizing: 'border-box',
              width: '100%',
              marginTop: '8px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(216, 134, 255, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(216, 134, 255, 0.4)';
            }}
          >
            Create account
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <Link
            to="/signin"
            style={{
              color: 'var(--pink)',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
